import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import './Meld.css';
import './App.css';
import NodeInfoPanel from './NodeInfoPanel';
import { Box, Typography } from '@mui/material';
import {
    NodeConfig,
    TooltipManager,
    wrapText,
    calculateNodeRadius,
    defineArrowMarkers,
    getLinkStatusFromValue,
    LinkTypes,
    utilities, isEvidenceFullyLinked
} from '../utils/MeldUtils';
import { useShimmer } from './ShimmerContext';
import { setupShimmerEffects, updateNodeShimmer } from '../utils/d3VisualEffects';
import { useTutorial } from './TutorialProvider';
import { useMELTranslations } from '../hooks/useMELTranslations';
import { useLanguage } from "../utils/LanguageContext";


const Meld = ({
                  currentMEL,
                  melData,
                  updateMELData,
                  isInfoPanelOpen,
                  toggleInfoPanel,
                  activeStudentModel
              }) => {
    const [data, setData] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height:0 });
    const [selectedModel, setSelectedModel] = useState(null);
    const [selectedEvidence, setSelectedEvidence] = useState(null);
    const [linkStatus, setLinkStatus] = useState(null);
    const [isNodeSelected, setIsNodeSelected] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isTranslating } = useMELTranslations(melData);
    const [forceUpdate, setForceUpdate] = useState(0);
    const svgRef = useRef(null);
    const gRef = useRef(null);
    const simulationRef = useRef(null);
    const dataRef = useRef(null);
    const updatePendingRef = useRef(false);
    const containerRef = useRef(null);
    const linkStatusRef = useRef(null);
    const tooltipManager = useRef(null);
    const { currentLanguage, getNodeTranslation, updateTrigger } = useLanguage();
    const { activeShimmer } = useShimmer();
    const { completeAction } = useTutorial();
    const modelIndicatorsRef = useRef(new Map());

    const languageContext = useMemo(() => ({
        currentLanguage,
        getNodeTranslation,
    }), [currentLanguage, getNodeTranslation]);

    // Initialize tooltip manager with language context
    useEffect(() => {
        tooltipManager.current = new TooltipManager(languageContext);
        return () => tooltipManager.current?.cleanup();
    }, [languageContext]);


    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({ width, height });
            }
        };

        // Call updateDimensions once to set initial size
        updateDimensions();

    }, []);


    useEffect(() => {
        if (!svgRef.current || dimensions.width === 0 || dimensions.height === 0) return;

        const svg = d3.select(svgRef.current)
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("preserveAspectRatio", "xMidYMid meet")
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`);

        svg.selectAll("*").remove();

        const g = svg.append("g");
        gRef.current = g;

        const zoom = d3.zoom()
            .scaleExtent([0.1, 4])
            .on("zoom", (event) => {
                g.attr("transform", event.transform);
            });

        svg.call(zoom);

        defineArrowMarkers(svg);
    }, [dimensions]);

    /* All useCallback functions here */

    const loadAndRenderModel = useCallback(() => {
        if (melData) {
            setData(JSON.parse(JSON.stringify(melData))); // Deep copy to avoid mutating the original data
            dataRef.current = JSON.parse(JSON.stringify(melData));
        } else {
            console.error('No data available for model');
        }
    }, [melData]);


    const updateNodeHighlighting = useCallback(() => {
        if (!gRef.current) return;

        gRef.current.selectAll('.node')
            .classed('selected', d =>
                (selectedModel && d.id === selectedModel.id) ||
                (selectedEvidence && d.id === selectedEvidence.id)
            );
    }, [selectedModel, selectedEvidence]);

    const getLinkStatus = useCallback((source, target) => {
        if (!data || !data.links) {
            console.log('No data or links available');
            return 'no link';
        }
        const link = data.links.find(l => {
            const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
            const targetId = typeof l.target === 'object' ? l.target.id : l.target;
            return (sourceId === source.id && targetId === target.id) ||
                (sourceId === target.id && targetId === source.id);
        });

        if (link) {
            return getLinkStatusFromValue(link.value);
        }
        return 'no link';
    }, [data]);

    const updateLinkStatus = useCallback(() => {
        if (!data || !selectedModel || !selectedEvidence) {
            setLinkStatus(null);
            linkStatusRef.current = null;
            return;
        }

        const newStatus = getLinkStatus(selectedModel, selectedEvidence);
        setLinkStatus(newStatus);
        linkStatusRef.current = newStatus;

    }, [data, selectedModel, selectedEvidence, getLinkStatus]);

    const handleNodeClick = useCallback((event, d) => {
        event.stopPropagation();

        // Make sure plausibility is up-to-date whenever node selections change
        if (data) {
            utilities.recalculatePlausibility(data);
        }

        if (d.type === "model") {
            setSelectedModel(prev => {
                const newSelection = prev && prev.id === d.id ? null : d;
                if (newSelection) {
                    completeAction('modelNode.select');
                }
                return newSelection;
            });
        } else if (d.type === "evidence") {
            setSelectedEvidence(prev => {
                const newSelection = prev && prev.id === d.id ? null : d;
                if (newSelection) {
                    completeAction('evidenceNode.select');
                }
                return newSelection;
            });
        }

        setIsNodeSelected(true);
        setSelectedNode(d);

        updateNodeHighlighting();
        updateLinkStatus();
        tooltipManager.current?.hide();

        simulationRef.current?.alpha(0.1).alphaTarget(0).restart();

        setTimeout(() => {
            setIsNodeSelected(false);
            simulationRef.current?.alpha(0.3).alphaTarget(0).restart();
        }, 300);
    }, [completeAction, updateNodeHighlighting, updateLinkStatus, data]);


    const updateGraph = useCallback(() => {
        if (!gRef.current || !data || !svgRef.current) return;

        const g = gRef.current;
        const svg = d3.select(svgRef.current);

        setupShimmerEffects(svg);
        defineArrowMarkers(svg);

        const width = svgRef.current.clientWidth;
        const height = svgRef.current.clientHeight;

        svg.attr("width", width).attr("height", height);

        // Create separate groups for links and nodes
        let linkGroup = g.select('.link-group');
        let nodeGroup = g.select('.node-group');

        // If groups don't exist, create them
        if (linkGroup.empty()) {
            linkGroup = g.append('g').attr('class', 'link-group');
        }
        if (nodeGroup.empty()) {
            nodeGroup = g.append('g').attr('class', 'node-group');
        }

        // Update existing links or add new ones
        const link = linkGroup.selectAll(".link")
            .data(data.links.filter(d => d.value !== LinkTypes.NO_EFFECT_EXPLICIT.value),
                d => `${d.source.id || d.source}-${d.target.id || d.target}`);

        link.exit().remove();

        const linkEnter = link.enter().append("line")
            .attr("class", "link")
            .attr("stroke", "#999")
            .attr("stroke-opacity", 0.6);

        link.merge(linkEnter)
            .attr("stroke", "#999")
            .attr("stroke-opacity", 0.6)
            .attr("stroke-width", d => Math.abs(d.value) === 2 ? 4 : 2)
            .attr("stroke-dasharray", d => d.value < 0 ? "8,8" : "none")
            .attr("marker-end", d => {
                const target = data.nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
                if (target && target.type === "model") {
                    return Math.abs(d.value) === 2 ? "url(#end-arrow-strong)" : "url(#end-arrow)";
                }
                return null;
            });

        // Update existing nodes or add new ones
        const node = nodeGroup.selectAll(".node")
            .data(data.nodes, d => d.id);

        node.exit().remove();

        const nodeEnter = node.enter().append("g")
            .attr("class", d => `node ${d.type}`)
            .call(d3.drag()
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended))
            .on("click",  handleNodeClick)
            .on('mouseover', function(event, d) {
                tooltipManager.current.show(event, d, data.nodes, data.links);
            })
            .on("mouseout", function(d) {
                tooltipManager.current.hide();
            })

        nodeEnter.each(function(d) {
            const node = d3.select(this);
            const isShimmerActive = (d.type === 'model' && activeShimmer === 'modelNode') ||
                (d.type === 'evidence' && activeShimmer === 'evidenceNode');

            node.classed('shimmer-active', isShimmerActive);

            if (d.type === 'evidence') {
                // Create text element and wrap it
                const tempText = node.append('text')
                    .attr('dy', '.1em')
                    .attr('text-anchor', 'middle')
                    .attr('class', 'node-label')
                    .text(d => NodeConfig.getDisplayText(d, languageContext))
                    .call(wrapText, NodeConfig.textWrapWidth);
                // Get the actual height by measuring all tspans
                const tspans = tempText.selectAll('tspan');
                const lineCount = tspans.nodes()
                    .filter(tspan => tspan.textContent.trim().length > 0)
                    .length;
                const totalTextHeight =  NodeConfig.textHeight * lineCount * NodeConfig.lineHeight;

                // Get the widest tspan
                let maxWidth = 0;
                tspans.each(function() {
                    const width = this.getComputedTextLength();
                    maxWidth = Math.max(maxWidth, width);
                });

                tempText.remove();

                // Calculate rectangle dimensions with padding
                const width = maxWidth + (NodeConfig.padding * 2);
                const height = totalTextHeight + (NodeConfig.padding * 2);

                // Add rounded rectangle sized to wrapped text
                node.append('rect')
                    .attr('class', 'evidence-rect')
                    .attr('x', -width/2)
                    .attr('y', -height/2)
                    .attr('width', width)
                    .attr('height', height)
                    .attr('rx', NodeConfig.cornerRadius)
                    .attr('ry', NodeConfig.cornerRadius);

                // Add actual text centered in rectangle
                node.append('text')
                    .attr('dy', (1 - lineCount * NodeConfig.lineHeight / 2) + 'em')
                    .attr('text-anchor', 'middle')
                    .attr('class', 'node-label')
                    .text(d => NodeConfig.getDisplayText(d, languageContext))
                    .call(wrapText, NodeConfig.textWrapWidth);


            } else {
                // Model nodes
                node.append('circle')
                    .attr('class', 'node-circle model-circle')
                    .attr('r', d => calculateNodeRadius(d, data.links));

                // For model nodes, add alphabetical indicator
                node.append('text')
                    .attr('dy', '.1em')
                    .attr('text-anchor', 'middle')
                    .attr('class', 'node-label')
                    .text(d => {
                        if (d.type === 'model') {
                            // Check if we already have an indicator for this model
                            if (!modelIndicatorsRef.current.has(d.id)) {
                                // Get all model nodes
                                const modelNodes = data.nodes.filter(n => n.type === 'model');
                                // Find index of current model node
                                const modelIndex = modelNodes.findIndex(n => n.id === d.id);
                                // Convert index to alphabetical indicator (A, B, C, etc.)
                                modelIndicatorsRef.current.set(d.id, String.fromCharCode(65 + modelIndex));
                            }
                            return modelIndicatorsRef.current.get(d.id) + ': ' + NodeConfig.getDisplayText(d, languageContext);
                        } else {
                            return NodeConfig.getDisplayText(d, languageContext);
                        }
                    })
                    .call(wrapText, NodeConfig.textWrapWidth);
            }
        });

        // Update all nodes (both existing and new)
        const allNodes = node.merge(nodeEnter);

        // Update node appearance
        allNodes.each(function(d) {
            const node = d3.select(this);
            const isShimmerActive = (d.type === 'model' && activeShimmer === 'modelNode') ||
                (d.type === 'evidence' && activeShimmer === 'evidenceNode');

            if (d.type === 'evidence') {
                // Check if this evidence node is fully linked
                const isFullyLinked = isEvidenceFullyLinked(d, data.nodes, data.links);

                // Update rectangle styling
                node.select('rect')
                    .attr('class', 'evidence-rect')
                    .style('fill', isFullyLinked ? NodeConfig.fullyLinkedFillColor : NodeConfig.normalEvidenceFillColor)
            } else {
                // Model node styling
                node.select('circle')
                    .attr('class', 'node-circle model-circle')
                    .attr('r', d => calculateNodeRadius(d, data.links));
            }

            updateNodeShimmer(node, d, isShimmerActive);
        });

        allNodes.select("circle")
            .attr("r", d => calculateNodeRadius(d, data.links));

       // allNodes.select("text")
        //    .text(d => NodeConfig.getDisplayText(d, languageContext))
        //    .call(wrapText, NodeConfig.textWrapWidth);

        // Custom force for disconnected nodes
        const forceDisconnected = () => {
            return (alpha) => {
                data.nodes.forEach(d => {
                    if (d.type === 'evidence' && !data.links.some(l => l.source === d || l.target === d)) {
                        d.x += (width * 0.25 - d.x) * alpha;
                    } else if (d.type === 'model' && !data.links.some(l => l.source === d || l.target === d)) {
                        d.x += (width * 0.75 - d.x) * alpha;
                    }
                });
            };
        };

        // Custom force for repelling evidence nodes from large-radius models
        const forceRepelEvidence = () => {
            return (alpha) => {
                data.links.forEach(link => {
                    const source = link.source.id ? link.source : data.nodes.find(n => n.id === link.source);
                    const target = link.target.id ? link.target : data.nodes.find(n => n.id === link.target);

                    if (source.type === 'model' && target.type === 'evidence') {
                        const dx = target.x - source.x;
                        const dy = target.y - source.y;
                        const distance = Math.sqrt(dx * dx + dy * dy);
                        const modelRadius = calculateNodeRadius(source,data.links);
                        const desiredDistance = modelRadius * 3; // Adjust this multiplier as needed

                        if (distance < desiredDistance) {
                            const force = (desiredDistance - distance) / distance * alpha;
                            target.x += dx * force;
                            target.y += dy * force;
                        }
                    } else if (source.type === 'evidence' && target.type === 'model') {
                        const dx = source.x - target.x;
                        const dy = source.y - target.y;
                        const distance = Math.sqrt(dx * dx + dy * dy);
                        const modelRadius = calculateNodeRadius(target,data.links);
                        const desiredDistance = modelRadius * 3; // Adjust this multiplier as needed

                        if (distance < desiredDistance) {
                            const force = (desiredDistance - distance) / distance * alpha;
                            source.x += dx * force;
                            source.y += dy * force;
                        }
                    }
                });
            };
        };

        // Custom force for repelling model nodes from each other
        const forceRepelModels = () => {
            let nodes;
            let strength = isNodeSelected ? 100 : 300; // Reduce strength when a node is selected
            let distanceMin = 50;
            let distanceMax = 200;

            function force(alpha) {
                const quadtree = d3.quadtree()
                    .x(d => d.x)
                    .y(d => d.y)
                    .addAll(nodes);

                for (const d of nodes) {
                    const nodeType = d.type || (d.data && d.data.type);
                    if (nodeType === 'model') {
                        quadtree.visit((q, x1, y1, x2, y2) => {
                            if (!q.length) {
                                do {
                                    const qNodeType = q.data.type || (q.data && q.data.data && q.data.data.type);
                                    if (qNodeType === 'model' && q.data !== d) {
                                        let x = d.x - q.data.x,
                                            y = d.y - q.data.y,
                                            l = Math.sqrt(x * x + y * y);

                                        if (l < distanceMax) {
                                            l = Math.max(distanceMin, l);
                                            const k = (1 - Math.pow(l / distanceMax, 2)) * alpha * strength;
                                            x *= k / l;
                                            y *= k / l;

                                            const damping = 0.5;
                                            d.vx = (d.vx || 0) * damping + x;
                                            d.vy = (d.vy || 0) * damping + y;
                                            q.data.vx = (q.data.vx || 0) * damping - x;
                                            q.data.vy = (q.data.vy || 0) * damping - y;

                                            d.x += d.vx;
                                            d.y += d.vy;
                                            q.data.x -= q.data.vx;
                                            q.data.y -= q.data.vy;
                                        }
                                    }
                                } while ((q = q.next));
                            }
                            return x1 > d.x + distanceMax || x2 < d.x - distanceMax ||
                                y1 > d.y + distanceMax || y2 < d.y - distanceMax;
                        });
                    }
                }
            }

            force.initialize = _ => nodes = _;

            return force;
        };

        // Update or create the simulation
        if (!simulationRef.current) {
            simulationRef.current = d3.forceSimulation()
                .force("link", d3.forceLink().id(d => d.id).distance(d => {
                    // Only apply force to non-NO_EFFECT links
                    if (d.value === LinkTypes.NO_EFFECT_EXPLICIT.value) {
                        return 0;
                    }
                    const source = d.source.id ? d.source : data.nodes.find(n => n.id === d.source);
                    const target = d.target.id ? d.target : data.nodes.find(n => n.id === d.target);
                    return (calculateNodeRadius(source,data.links) + calculateNodeRadius(target,data.links)) * 2;
                }))
                .force("charge", d3.forceManyBody().strength(-500))
                .force("center", d3.forceCenter(width / 2, height / 2))
                .force("collision", d3.forceCollide().radius(d => calculateNodeRadius(d,data.links) + NodeConfig.minCollidePad))
                .force("x", d3.forceX(width / 2).strength(0.05))
                .force("y", d3.forceY(height / 2).strength(0.05))
                .force("disconnected", forceDisconnected())
                .force("repelEvidence", forceRepelEvidence())
                .force("repelModels", forceRepelModels());
        }

        simulationRef.current
            .nodes(data.nodes)
            .force("link").links(data.links.filter(d => d.value !== LinkTypes.NO_EFFECT_EXPLICIT.value));

        // Update collision force
        simulationRef.current.force("collision", d3.forceCollide().radius(d => calculateNodeRadius(d,data.links) + 10));

        // Update forces
        simulationRef.current
            .force("center", d3.forceCenter(width / 2, height / 2))
            .force("x", d3.forceX(width / 2).strength(0.05))
            .force("y", d3.forceY(height / 2).strength(0.05))
            .force("collision", d3.forceCollide().radius(d => calculateNodeRadius(d,data.links) + 10))
            .force("disconnected", forceDisconnected())
            .force("repelEvidence", forceRepelEvidence());

        simulationRef.current.alpha(1).restart();

        simulationRef.current.on("tick", () => {

            link.merge(linkEnter)
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => {
                    const target = typeof d.target === 'object' ? d.target : data.nodes.find(n => n.id === d.target);
                    if (!target) return d.source.x;
                    const dx = target.x - d.source.x;
                    const dy = target.y - d.source.y;
                    const angle = Math.atan2(dy, dx);
                    const targetRadius = calculateNodeRadius(target,data.links);
                    return target.x - (targetRadius - 2) * Math.cos(angle);
                })
                .attr("y2", d => {
                    const target = typeof d.target === 'object' ? d.target : data.nodes.find(n => n.id === d.target);
                    if (!target) return d.source.y;
                    const dx = target.x - d.source.x;
                    const dy = target.y - d.source.y;
                    const angle = Math.atan2(dy, dx);
                    const targetRadius = calculateNodeRadius(target,data.links);
                    return target.y - (targetRadius - 2) * Math.sin(angle);
                });

            allNodes
                .attr("transform", d => `translate(${d.x},${d.y})`)
                .select("circle")
                .attr("r", d => calculateNodeRadius(d,data.links));
        });

        function dragstarted(event, d) {
            if (!event.active) simulationRef.current.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
        }

        function dragged(event, d) {
            d.fx = event.x;
            d.fy = event.y;
        }

        function dragended(event, d) {
            if (!event.active) simulationRef.current.alphaTarget(0);
            d.fx = null;
            d.fy = null;
        }


    }, [data, handleNodeClick, isNodeSelected, activeShimmer, languageContext ]);


    const handleDeleteNode = (nodeId) => {
        // Step 1: Set all links connected to the node to "no effect" (value of 0)
        let updatedLinks = melData.links.map(link => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;

            if (sourceId === nodeId || targetId === nodeId) {
                return { ...link, value: 0 };
            }
            return link;
        });

        // Update the model with the modified links
        let updatedMelData = {
            ...melData,
            links: updatedLinks
        };
        // Update the model data and re-render
        updateMELData(currentMEL, updatedMelData);
        // Step 2: After a short delay, remove the node and its links
        setTimeout(() => {
            const updatedNodes = melData.nodes.filter(node => node.id !== nodeId);
            updatedLinks = updatedLinks.filter(link => {
                const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                const targetId = typeof link.target === 'object' ? link.target.id : link.target;
                return sourceId !== nodeId && targetId !== nodeId;
            });

            updatedMelData = {
                ...updatedMelData,
                nodes: updatedNodes,
                links: updatedLinks
            };
            updateMELData(currentMEL, updatedMelData);
            setSelectedNode(null);
        }, 100); // Short delay to allow D3 to process the link updates
    };

    // Function to clean up the data before sending to parent
    const cleanupData = useCallback((dirtyData) => {
        if (!dirtyData) return null;

        return {
            nodes: dirtyData.nodes.map(node => {
                const cleanNode = {
                    id: node.id,
                    type: node.type,
                    description: node.description,
                    references: node.references
                };

                // Include plausibility for model nodes
                if (node.type === 'model' && node.plausibility !== undefined) {
                    cleanNode.plausibility = node.plausibility;
                }

                return cleanNode;
            }),
            links: dirtyData.links.map(link => ({
                source: typeof link.source === 'object' ? link.source.id : link.source,
                target: typeof link.target === 'object' ? link.target.id : link.target,
                value: link.value
            }))
        };
    }, []);

    const updateParentData = useCallback(() => {
        if (data ) {//&& JSON.stringify(data) !== JSON.stringify(dataRef.current)) {
            const cleanData = cleanupData(data);
            updateMELData(currentMEL, cleanData);
            dataRef.current = JSON.parse(JSON.stringify(data));
        }
    }, [currentMEL, data, updateMELData, cleanupData]);

    const updateNode = useCallback((node, isRefUpdate = false) => {
        setData(prevData => {
            const newData = {...prevData};
            const nodeToUpdate = newData.nodes.find(n => n.id === node.id);
            if (nodeToUpdate) {
                if (isRefUpdate) {
                    // Only update references for reference-only updates
                    nodeToUpdate.references = node.references;
                } else {
                    // Full node update
                    Object.assign(nodeToUpdate, node);
                }
            }
            return newData;
        });
        // Only set update pending if it's not a reference update
        if (!isRefUpdate) {
            updatePendingRef.current = true;
        }
    }, []);

    const addReference = useCallback((node, newReference) => {
        setData(prevData => {
            const newData = {...prevData};
            const nodeToUpdate = newData.nodes.find(n => n.id === node.id);
            if (nodeToUpdate) {
                if (!nodeToUpdate.references) {
                    nodeToUpdate.references = [];
                }
                nodeToUpdate.references.push(newReference);
            }
            return newData;
        });
        updatePendingRef.current = true;
    }, []);

    const updateLink = useCallback((value) => {
        if (!selectedModel || !selectedEvidence || !data) {
            return;
        }

        // Modify the existing data object to preserve D3 positioning
        const updatedData = {...data}; // Shallow clone to trigger React updates

        // Filter out existing link between these nodes
        updatedData.links = data.links.filter(l => {
            const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
            const targetId = typeof l.target === 'object' ? l.target.id : l.target;
            return !(sourceId === selectedEvidence.id && targetId === selectedModel.id) &&
                !(sourceId === selectedModel.id && targetId === selectedEvidence.id);
        });

        // Add new link if it's not NO_LINK
        if (value !== LinkTypes.NO_LINK.value) {
            updatedData.links.push({
                source: selectedEvidence.id,
                target: selectedModel.id,
                value: value
            });
        }

        // Update plausibility in place without recreating node objects
        utilities.recalculatePlausibility(updatedData);

        // Update state with modified data
        setData(updatedData);
        setLinkStatus(getLinkStatusFromValue(value));
        updatePendingRef.current = true;

        completeAction('link.select');
    }, [selectedModel, selectedEvidence, data, completeAction]);


    const addNode = useCallback((newNodeData) => {
        if (!data || !newNodeData.id || !newNodeData.description) return;

        const newNode = {
            id: newNodeData.id,
            description: newNodeData.description,
            type: newNodeData.type
        };

        setData(prevData => ({
            ...prevData,
            nodes: [...prevData.nodes, newNode]
        }));
        updatePendingRef.current = true;

    }, [data]);

    /* end useCallback section */
    /* ALL useEffect hooks here */

    // Force re-render when language changes
    useEffect(() => {
        if (data) {
            updateGraph();
        }
    }, [data, currentLanguage, updateGraph, updateTrigger]);

    // Effect for initial loading and updating model data
    useEffect(() => {
        if (melData) {
            // Create a deep copy to avoid mutating the original data
            const clonedData = JSON.parse(JSON.stringify(melData));

            // Initialize plausibility values for all model nodes
            utilities.recalculatePlausibility(clonedData);
            
            // Get model nodes and sort them by ID to ensure consistent ordering
            const modelNodes = clonedData.nodes
                .filter(node => node.type === 'model')
                .sort((a, b) => a.id.localeCompare(b.id));
            
            // Assign indicators based on initial position
            modelNodes.forEach((node, index) => {
                if (!modelIndicatorsRef.current.has(node.id)) {
                    const letter = String.fromCharCode(65 + index);
                    modelIndicatorsRef.current.set(node.id, letter);
                }
            });

            setData(clonedData);
            dataRef.current = JSON.parse(JSON.stringify(clonedData));

            console.log('Model data loaded with plausibility values:', clonedData);
        }
    }, [melData]);

    // Separate effect to handle selection changes
    useEffect(() => {
        if (data && (selectedModel || selectedEvidence)) {
            // When selection changes, ensure selected nodes have current data
            const currentData = { ...data }; // Shallow clone to avoid issues

            // Update plausibility values
            utilities.recalculatePlausibility(currentData);

            // If model is selected, get its fresh version from the updated data
            if (selectedModel) {
                const updatedModel = currentData.nodes.find(n => n.id === selectedModel.id);
                if (updatedModel) {
                    setSelectedModel(updatedModel);
                    console.log('Updated selected model with plausibility:', updatedModel.plausibility);
                }
            }

            // Update link status as well
            if (selectedModel && selectedEvidence) {
                const currentStatus = getLinkStatus(selectedModel, selectedEvidence);
                setLinkStatus(currentStatus);
            }
        }
    }, [data, selectedModel, selectedEvidence, getLinkStatus]);

    useEffect(() => {
        updateLinkStatus();
        updateNodeHighlighting();
    }, [selectedModel, selectedEvidence, updateLinkStatus, updateNodeHighlighting, data]);


    useEffect(() => {
        loadAndRenderModel();
    }, [currentMEL, loadAndRenderModel]);

    useEffect(() => {
        if (data) {
            updateGraph();
        }
    }, [data, currentLanguage, updateGraph, forceUpdate, activeShimmer]);

    useEffect(() => {
        if (updatePendingRef.current) {
            updateParentData();
            //updateGraph();
            updatePendingRef.current = false;
        }
    }, [data, updateParentData, updateGraph]);

    useEffect(() => {
        let resizeTimer;
        const handleResize = () => {
            // Clear the existing timer
            clearTimeout(resizeTimer);

            // Set a new timer to update after resize settles
            resizeTimer = setTimeout(() => {
                if (svgRef.current && gRef.current && data) {
                    const svg = d3.select(svgRef.current);
                    const width = svgRef.current.clientWidth;
                    const height = svgRef.current.clientHeight;

                    // Update SVG dimensions
                    svg.attr("width", width)
                        .attr("height", height);

                    // Update force simulation center
                    if (simulationRef.current) {
                        simulationRef.current
                            .force("center", d3.forceCenter(width / 2, height / 2))
                            .alpha(0.3)
                            .restart();
                    }

                    setForceUpdate(prev => prev + 1);
                }
            }, 250); // Wait for resize to settle
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimer);
        };
    }, [data]);

    useEffect(() => {
        if (!gRef.current || !data) return;

        gRef.current.selectAll('.node-label')
            .text(d => {
                if (d.type === 'model') {
                    // Use stored indicator or generate if not present
                    if (!modelIndicatorsRef.current.has(d.id)) {
                        // Get all model nodes and sort them
                        const modelNodes = data.nodes
                            .filter(n => n.type === 'model')
                            .sort((a, b) => a.id.localeCompare(b.id));
                        
                        // Find index of current model node
                        const modelIndex = modelNodes.findIndex(n => n.id === d.id);
                        
                        // Convert index to alphabetical indicator (A, B, C, etc.)
                        modelIndicatorsRef.current.set(d.id, String.fromCharCode(65 + modelIndex));
                    }
                    return modelIndicatorsRef.current.get(d.id) + ': ' + NodeConfig.getDisplayText(d, languageContext);
                } else {
                    return NodeConfig.getDisplayText(d, languageContext);
                }
            })
            .call(wrapText, NodeConfig.textWrapWidth);

    }, [currentLanguage, data, languageContext]);

    // Reset indicators when currentMEL changes
    useEffect(() => {
        // Clear the indicators map when switching to a different MEL
        modelIndicatorsRef.current.clear();
    }, [currentMEL]);

    /* end useEffects section */

    return (
        <div className="meld-container">
            {activeStudentModel && (
                <Box sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1,
                    bgcolor: 'warning.light',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Viewing: {activeStudentModel}
                    </Typography>
                </Box>
            )}

            <div ref={containerRef} className="meld-graphics" style={{width: '100%', height: '100%'}}>
                {dimensions.width > 0 && dimensions.height > 0 && (
                    <svg ref={svgRef}></svg>
                )}
            </div>

            <div className={`meld-info-panel ${isInfoPanelOpen ? 'open' : ''}`}>
                <NodeInfoPanel
                    selectedModel={selectedModel}
                    selectedEvidence={selectedEvidence}
                    links={data?.links}
                    linkStatus={linkStatus}
                    onUpdateNode={updateNode}
                    onAddNode={addNode}
                    onAddReference={addReference}
                    onUpdateLink={updateLink}
                    onDelete={handleDeleteNode}
                    toggleInfoPanel={toggleInfoPanel}
                />
            </div>
        </div>
    );
};
export default Meld;